import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import { THEME_COLORS } from '../constants/theme';
import { DOCUMENT } from '../constants/documentTheme';
import { partnerOutlineBtnSx } from './PartnerListChrome';
import {
  PARTNER_DEVELOPER_API_MONTHLY,
  PARTNER_ONBOARDING_TIER_TYPE,
  PARTNER_PROFESSIONAL_ONBOARDING_FEE,
  partnerDeveloperApiFeatureKeys,
  partnerOnboardingFeatureKeys
} from '../config/plans';

const PartnerAddonCard = ({
  eyebrow,
  title,
  price,
  perLabel,
  featureKeys,
  ctaLabel,
  onPurchase,
  loading,
  disabled = false,
  variant = 'modal'
}) => {
  const { t } = useTranslation();
  const isBilling = variant === 'billing';

  const features = featureKeys.map((key) => (
    <Typography
      key={key}
      variant={isBilling ? undefined : 'body2'}
      sx={isBilling ? { fontSize: 13, color: DOCUMENT.ink2, mb: 0.6 } : { mb: 0.5 }}
    >
      ✓ {t(key)}
    </Typography>
  ));

  const ctaButton = (
    <Button
      fullWidth
      variant={isBilling ? undefined : 'outlined'}
      disabled={loading || disabled}
      onClick={onPurchase}
      sx={
        isBilling
          ? { ...partnerOutlineBtnSx, mt: 1.5, width: '100%' }
          : {
              mt: 1.5,
              borderColor: THEME_COLORS.primary.main,
              color: THEME_COLORS.primary.main,
              '&:hover': {
                borderColor: THEME_COLORS.primary.dark,
                bgcolor: 'rgba(89, 23, 22, 0.04)'
              }
            }
      }
    >
      {ctaLabel}
    </Button>
  );

  if (isBilling) {
    return (
      <Box
        sx={{
          p: 2.5,
          bgcolor: 'grey.50',
          border: `1px solid ${DOCUMENT.ruleHard}`,
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Typography sx={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
          {eyebrow}
        </Typography>
        <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 26, fontWeight: 400, mt: 0.75 }}>
          {title}
        </Typography>
        <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 22, mt: 0.5, color: DOCUMENT.ink }}>
          ${price}
          <Box component="span" sx={{ fontFamily: DOCUMENT.sans, fontSize: 12, color: DOCUMENT.ink3, ml: 0.75 }}>
            {perLabel}
          </Box>
        </Typography>
        <Box sx={{ mt: 1.75, flex: 1 }}>{features}</Box>
        <Box sx={{ minHeight: 28, mt: 1.5 }} />
        {ctaButton}
      </Box>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: 'divider',
        bgcolor: 'grey.50',
        overflow: 'visible',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <CardContent
        sx={{
          pt: 2,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }}
      >
        <Typography
          variant="overline"
          sx={{ display: 'block', color: 'text.secondary', letterSpacing: '0.12em', mb: 0.5 }}
        >
          {eyebrow}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 800, my: 1 }}>
          ${price}
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.75 }}>
            {perLabel}
          </Typography>
        </Typography>
        <Box sx={{ flex: 1 }}>{features}</Box>
        <Box sx={{ minHeight: 28, mt: 1 }} />
        {ctaButton}
      </CardContent>
    </Card>
  );
};

const PartnerOnboardingCard = ({ onPurchase, loading, disabled = false, variant = 'modal' }) => {
  const { t } = useTranslation();
  return (
    <PartnerAddonCard
      variant={variant}
      eyebrow={t('partnerPlans.onboardingCard.eyebrow')}
      title={t('partnerPlans.onboardingCard.title')}
      price={PARTNER_PROFESSIONAL_ONBOARDING_FEE}
      perLabel={t('partnerPlans.onboardingCard.perOnce')}
      featureKeys={partnerOnboardingFeatureKeys}
      ctaLabel={t('partnerPlans.onboardingCard.cta')}
      onPurchase={onPurchase}
      loading={loading}
      disabled={disabled}
    />
  );
};

const PartnerDeveloperApiCard = ({
  onPurchase,
  loading,
  included = false,
  canPurchase = true,
  variant = 'modal'
}) => {
  const { t } = useTranslation();
  return (
    <PartnerAddonCard
      variant={variant}
      eyebrow={t('partnerPlans.developerApi.eyebrow')}
      title={t('partnerPlans.developerApi.cardTitle')}
      price={PARTNER_DEVELOPER_API_MONTHLY}
      perLabel={t('partnerPlans.perMonth')}
      featureKeys={partnerDeveloperApiFeatureKeys}
      ctaLabel={included
        ? t('partnerPlans.developerApi.ctaIncluded')
        : canPurchase
          ? t('partnerPlans.developerApi.cta')
          : t('partnerPlans.developerApi.ctaRequiresAgency')}
      onPurchase={onPurchase}
      loading={loading}
      disabled={included || !canPurchase}
    />
  );
};

export { PARTNER_ONBOARDING_TIER_TYPE, PartnerDeveloperApiCard };
export default PartnerOnboardingCard;
